import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { clone, applySnapshot } from 'mobx-state-tree';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SettingsFormHeader } from '../../../common/SettingsFormHeader';
import { EditPopup } from '../styled';
import Button from '../../../common/Button';
import { PolicyItem } from './PolicyItem';
import CustomInput from '../../../common/Input';
import { validatePolicySelectedOptions, isBtnDisabled } from './utils';
import { ShippingForm, PaymentForm, ReturnForm } from './PolicyEdit';
import { POLICY_KEYS_TYPES } from '../../../../constants/settings-model';
import CheckboxGroup from './CheckboxGroup';

@injectIntl
@inject('settingsStore')
@observer
export class PolicySelectPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      policySelectedOptionsClone: null,
      creating: props.settingsStore.policies.selectedPolicySet.isNew,
      isFormOpen: false,
      policyType: '',
      isCreateAction: true,
    };
    this.cancelPolicyOptions = this.cancelPolicyOptions.bind(this);
    this.savePolicyOptions = this.savePolicyOptions.bind(this);
    this.createNewPolicy = this.createNewPolicy.bind(this);
    this.closePopup = this.closePopup.bind(this);
    this.openForm = this.openForm.bind(this);
    this.closeForm = this.closeForm.bind(this);
  }

  componentDidMount() {
    const { settingsStore, intl, updateBreadcrumbs } = this.props;
    const { selectedPolicySet } = settingsStore.policies;
    const breadcrumb = {
      name: intl.formatMessage({ id: 'listing.editPolicy' }),
    };
    updateBreadcrumbs(breadcrumb, false);
    selectedPolicySet.fetchPolicySelectedOptions();
  }

  static getDerivedStateFromProps(props, state) {
    const { selectedPolicySet } = props.settingsStore.policies;
    if (selectedPolicySet) {
      const { policySelectedOptions } = selectedPolicySet;
      if (state.policySelectedOptionsClone === null && state.creating) {
        return { policySelectedOptionsClone: clone(policySelectedOptions) };
      } else if (
        state.policySelectedOptionsClone === null &&
        validatePolicySelectedOptions(policySelectedOptions)
      ) {
        return { policySelectedOptionsClone: clone(policySelectedOptions) };
      }
    }
    return null;
  }

  componentWillUnmount() {
    this.setState({ policySelectedOptionsClone: null });
  }

  savePolicyOptions() {
    const { settingsStore } = this.props;
    settingsStore.policies.selectedPolicySet
      .savePolicyNewOptions()
      .then(() => this.setState({ policySelectedOptionsClone: null }, () => this.closePopup()));
  }

  createNewPolicy() {
    const { settingsStore, selectedDestId } = this.props;
    const { selectedPolicySet } = settingsStore.policies;
    if (validatePolicySelectedOptions(selectedPolicySet.policySelectedOptions)) {
      const { selectedAccountCode, selectedSrcId } = this.props;
      selectedPolicySet
        .createNewPolicySet(selectedAccountCode, selectedSrcId, selectedDestId)
        .then(() =>
          this.setState({ policySelectedOptionsClone: null, creating: false }, () =>
            this.closePopup(),
          ),
        );
    }
  }

  cancelPolicyOptions() {
    const { policySelectedOptionsClone } = this.state;
    const { settingsStore } = this.props;
    const { policySelectedOptions } = settingsStore.policies.selectedPolicySet;
    if (policySelectedOptionsClone) {
      applySnapshot(policySelectedOptions, policySelectedOptionsClone);
      this.setState({ policySelectedOptionsClone: null });
    }
  }

  closePopup() {
    const { closePopup, settingsStore } = this.props;
    settingsStore.policies.resetList();
    settingsStore.policies.fetchPolicyData();
    closePopup();
  }

  openForm({ policyType, isCreateAction = true }) {
    this.setState({ isFormOpen: true, policyType, isCreateAction });
  }

  closeForm() {
    this.setState({ isFormOpen: false, policyType: null });
  }

  renderPolicies() {
    const {
      settingsStore: { policies },
      intl,
    } = this.props;
    const { policySelectedOptionsClone, creating } = this.state;
    const policy = policies.selectedPolicySet;
    const { policyNormalizedOptions, policySelectedOptions } = policy;
    const disabled = isBtnDisabled(policySelectedOptionsClone, policySelectedOptions);
    return (
      <EditPopup>
        <SettingsFormHeader
          id="listing.policyName"
          closeButton={{ clickHandler: this.closePopup, type: 'exit' }}
          marginTop="0"
          marginBottom="19px"
          values={{
            policyName:
              policy.isNew && creating ? (
                <CustomInput
                  onChange={policy.setPolicyName}
                  type="text"
                  value={policySelectedOptions.name}
                />
              ) : (
                policy.name
              ),
          }}
          defaultMessage="Policy Name: {policyName}"
        />
        {Object.keys(policies.policySetOptions).map(key => (
          <PolicyItem
            policyType={policies.policySetOptions[key]}
            policyKeyName={key}
            styles={{ marginBottom: '20px' }}
            key={key}
            policy={policy}
            selectedValues={policyNormalizedOptions ? policyNormalizedOptions[key] : null}
            openEditForm={this.openForm}
          />
        ))}
        <CheckboxGroup policy={policy} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Button
              cssProps={{ marginRight: '5px' }}
              clickHandler={policy.isNew ? this.createNewPolicy : this.savePolicyOptions}
              disabled={disabled}
            >
              {intl.formatMessage({
                id: policy.isNew ? 'common.create' : 'common.save',
              })}
            </Button>
            <Button clickHandler={this.cancelPolicyOptions} disabled={disabled} type="secondary">
              <FormattedMessage id="common.reset" defaultMessage="Reset" />
            </Button>
          </div>
          <Button clickHandler={this.closePopup} type="secondary">
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
        </div>
      </EditPopup>
    );
  }

  renderPolicyEditForm() {
    const { policyType, isCreateAction } = this.state;
    const policy = this.props.settingsStore.policies.selectedPolicySet;
    const { SHIPPING, PAYMENT, RETURNS } = POLICY_KEYS_TYPES;
    return (
      <EditPopup>
        <SettingsFormHeader
          id={isCreateAction ? 'listing.addNewBusinessPolicy' : 'common.save'}
          defaultMessage="Edit Policy"
          closeButton={{ clickHandler: this.closeForm, type: 'exit' }}
          marginTop="0px"
          marginBottom="19px"
        />
        {policyType === SHIPPING && (
          <ShippingForm
            isCreateAction={isCreateAction}
            close={this.closeForm}
            ebayShippingPolicies={policy.getEbayShippingForm(isCreateAction)}
          />
        )}
        {policyType === PAYMENT && (
          <PaymentForm
            isCreateAction={isCreateAction}
            close={this.closeForm}
            ebayPaymentPolicies={policy.getEbayPaymentForm(isCreateAction)}
          />
        )}
        {policyType === RETURNS && (
          <ReturnForm
            isCreateAction={isCreateAction}
            close={this.closeForm}
            ebayReturnPolicies={policy.getEbayReturnsForm(isCreateAction)}
          />
        )}
      </EditPopup>
    );
  }

  render() {
    const { isFormOpen } = this.state;
    return <Fragment>{isFormOpen ? this.renderPolicyEditForm() : this.renderPolicies()}</Fragment>;
  }
}

PolicySelectPanel.propTypes = {
  closePopup: PropTypes.func.isRequired,
  settingsStore: PropTypes.object,
  intl: PropTypes.objectOf(PropTypes.shape),
  selectedAccountCode: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  selectedSrcId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  updateBreadcrumbs: PropTypes.func,
};
